/* [04]-STUDIOS™ MORSE SIGNATURE ENGINE | V4
   ITU-standard dots and dashes. Decorative-only, reversible and merchant-configurable.
   No Shopify product options, buttons, cart mechanics or payment icons are replaced. */
(() => {
  'use strict';
  const NS='http://www.w3.org/2000/svg';
  const alphabet=Object.freeze({
    A:'.-',B:'-...',C:'-.-.',D:'-..',E:'.',F:'..-.',G:'--.',
    H:'....',I:'..',J:'.---',K:'-.-',L:'.-..',M:'--',
    N:'-.',O:'---',P:'.--.',Q:'--.-',R:'.-.',S:'...',
    T:'-',U:'..-',V:'...-',W:'.--',X:'-..-',Y:'-.--',Z:'--..',
    0:'-----',1:'.----',2:'..---',3:'...--',4:'....-',5:'.....',
    6:'-....',7:'--...',8:'---..',9:'----.'
  });
  const state={watch:null,settings:null,queued:false,observed:new WeakSet(),
    auto:new Set(), original:new WeakMap()};
  const observer=window.ResizeObserver ? new ResizeObserver(entries=>{
    for(const entry of entries) if(entry.target.isConnected) redraw(entry.target);
  }) : null;
  const config=()=>document.getElementById('MorseSystem04');
  const value=(key,fallback='')=>state.settings?.dataset[key] || fallback;
  const on=key=>value(key)==='true';
  const root=document.documentElement;
  function reflect(){
    state.settings=config();
    const overall=on('morseEnabled');
    root.dataset.morse04Enabled=String(overall);
    for(const key of ['auto','forms','product','sizes','patch','buttons','footer']){
      root.dataset['morse04'+key[0].toUpperCase()+key.slice(1)]=String(overall && on('morse'+key[0].toUpperCase()+key.slice(1)));
    }
    return overall;
  }
  function encode(text){
    const spans=[];let x=0;
    const words=String(text||'').toUpperCase().trim()
      .split(/\s+/).map(w=>[...w].filter(ch=>alphabet[ch])).filter(w=>w.length);
    words.forEach((word,wi)=>{
      word.forEach((ch,ci)=>{
        [...alphabet[ch]].forEach((symbol,si,symbols)=>{
          const length=symbol==='-'?3:1;
          spans.push([x,length]);x+=length;
          if(si<symbols.length-1)x++;
        });
        if(ci<word.length-1)x+=3;
      });
      if(wi<words.length-1)x+=7;
    });
    return {spans,units:x};
  }
  function stroke(parent,cls){
    let svg=parent.querySelector(':scope > svg.'+cls);
    if(!svg){
      svg=document.createElementNS(NS,'svg');
      svg.setAttribute('class',cls);
      svg.setAttribute('aria-hidden','true');
      svg.setAttribute('focusable','false');
      svg.setAttribute('preserveAspectRatio','none');
      const path=document.createElementNS(NS,'path');
      path.setAttribute('fill','none');
      path.setAttribute('stroke','currentColor');
      path.setAttribute('stroke-width','1');
      path.setAttribute('stroke-linecap','butt');
      svg.append(path);
      parent.append(svg);
    }
    if(!svg.querySelector('path')){
      const path=document.createElementNS(NS,'path');
      svg.append(path);
    }
    return svg;
  }
  function track(el){
    if(observer && !state.observed.has(el)){state.observed.add(el);observer.observe(el);}
  }
  function inkLine(node,selector,codeText,widthOverride){
    const svg=selector==='self'?node:node.querySelector(selector);
    if(!svg)return;
    const w=Math.round(widthOverride ?? node.getBoundingClientRect().width);
    if(w<25)return;
    const {spans,units}=encode(codeText);
    if(!units)return;
    const unit=Math.min(3.6,Math.max(.55,(w*.65)/units));
    const limit=units*unit;
    const dots=spans.map(([start,len])=>
      'M'+(start*unit).toFixed(2)+' 1H'+((start+len)*unit).toFixed(2)).join(' ');
    svg.setAttribute('viewBox','0 0 '+w+' 2');
    let line=svg.querySelector('path');
    line.setAttribute('d',dots);
    // Divider remains a functional separation, never an extra second line.
    const old=svg.querySelector('path.morse04-rest');
    const rest=limit+16<w ? 'M'+(limit+16).toFixed(2)+' 1H'+w : '';
    if(rest){
      const path=old || document.createElementNS(NS,'path');
      path.classList.add('morse04-rest');
      path.setAttribute('d',rest);
      path.setAttribute('fill','none');
      path.setAttribute('stroke','currentColor');
      path.setAttribute('stroke-opacity','.25');
      path.setAttribute('stroke-width','1');
      if(!old)svg.append(path);
    }else if(old) old.remove();
  }
  function point(distance,w,h,gutter){
    const x=w-2*gutter,y=h-2*gutter,perim=2*(x+y);
    let pos=((distance%perim)+perim)%perim;
    if(pos<=x)return [gutter+pos,gutter];
    pos-=x;if(pos<=y)return [w-gutter,gutter+pos];
    pos-=y;if(pos<=x)return [w-gutter-pos,h-gutter];
    pos-=x;return [gutter,h-gutter-pos];
  }
  function edge(node,message){
    const svg=stroke(node,'morse04-edge');
    const w=Math.round(node.clientWidth),h=Math.round(node.clientHeight);
    if(w<40||h<20)return;
    const gutter=5,perimeter=2*((w-2*gutter)+(h-2*gutter));
    const {spans,units}=encode(message);
    if(!units)return;
    const gap=8,repeat=Math.max(1,Math.round(perimeter/((units+gap)*4.3)));
    const unit=perimeter/(repeat*(units+gap));
    let paths=[];
    for(let rep=0;rep<repeat;rep++){
      for(const [start,len] of spans){
        let a=(rep*(units+gap)+start)*unit;
        const end=a+len*unit;
        const p=point(a,w,h,gutter);
        let segment='M'+p[0].toFixed(2)+' '+p[1].toFixed(2);
        const x=w-2*gutter,y=h-2*gutter;
        const corners=[x,x+y,2*x+y,perimeter];
        while(a<end-.0001){
          let next=corners.find(v=>v>a+.0001) || perimeter;
          a=Math.min(next,end);
          let to=point(a===perimeter?0:a,w,h,gutter);
          segment+=' L'+to[0].toFixed(2)+' '+to[1].toFixed(2);
        }
        paths.push(segment);
      }
    }
    svg.setAttribute('viewBox','0 0 '+w+' '+h);
    svg.querySelector('path').setAttribute('d',paths.join(' '));
    svg.querySelector('path.morse04-rest')?.remove();
  }
  function identifyHeading(text){
    const t=(text||'').toLowerCase();
    if(/material/.test(t))return 'morseMaterials';
    if(/size|fit|tagli/.test(t))return 'morseFit';
    if(/try|prova/.test(t))return 'morseTry';
    if(/shipping|return|spedizion|resi/.test(t))return 'morseShipping';
    return 'morseDetails';
  }
  function product(){
    const dividers=document.querySelectorAll(
      'body.template-product #MainContent .product-details .accordion--dividers summary.details__header');
    dividers.forEach((row,i)=>{
      const svg=stroke(row,'morse04-divider');
      const key=identifyHeading(row.textContent);
      const fallback=['04','STU','DIOS','TRI','ESTE'][i%5];
      const msg=value(key,fallback);
      row.dataset.morseMessage=msg;
      row.dataset.morseReady='true';
      track(row);
      inkLine(row,':scope > svg.morse04-divider',msg);
    });
  }
  function footerHeadings(){
    const cases=[
      ['.f4-acc--manual','morseFooterManual','04'],
      ['.f4-acc--legal','morseFooterLegals','STUDIOS'],
      ['.f4-col--follow','morseFooterFollow','TRI'],
      ['.f4-col--company','morseFooterCompany','ESTE']
    ];
    for(const [selector,key,fallback]of cases){
      document.querySelectorAll('footer.footer-04 '+selector).forEach(details=>{
        const summary=details.querySelector(':scope > summary.f4-acc__summary');
        if(!summary)return;
        const svg=stroke(summary,'morse04-footer--heading');
        svg.classList.add('morse04-footer');
        const msg=value(key,fallback);
        svg.dataset.morseMessage=msg;
        details.dataset.footerMorse=msg;
        track(summary);
        inkLine(summary,':scope > svg.morse04-footer--heading',msg);
      });
    }
  }
  function edges(){
    document.querySelectorAll('.patch04').forEach(card=>{
      track(card);edge(card,card.hasAttribute('data-morse-message')
        && card.dataset.morseMessage!=='04 STUDIOS' ? card.dataset.morseMessage
        : value('morsePatchMessage','04 STUDIOS'));
    });
    document.querySelectorAll('.pdp04-moss-patch .add-to-cart-button,.pc04-controls button,.pdpref04-dock__buy').forEach(btn=>{
      track(btn);edge(btn,value('morseButtonMessage','BUY EARLY'));
    });
  }
  function originalBoundary(node,edge){
    if(!state.original.has(node))state.original.set(node,{});
    const original=state.original.get(node);
    if(!original[edge]){
      const style=edge==='top'?'border-top-color':'border-bottom-color';
      original[edge]={name:style,value:node.style.getPropertyValue(style),
        priority:node.style.getPropertyPriority(style)};
    }
    node.style.setProperty(edge==='top'?'border-top-color':'border-bottom-color','transparent','important');
  }
  function makeSeam(node,edge,message){
    if(!node||node.matches('hr'))return;
    if(node.closest('button, label, a, input, select, textarea, .product-card, .patch04, [role="button"]'))return;
    if(!node.isConnected)return;
    // Never change fixed, sticky or anchored positioning used by product UX.
    const positioning=getComputedStyle(node).position;
    if(positioning==='fixed'||positioning==='sticky'||positioning==='absolute')return;
    const width=node.getBoundingClientRect().width;
    if(width<Math.max(140,window.innerWidth*.25))return;
    const opposite=edge==='top'?'bottom':'top';
    if(node.dataset.morse04Edge && node.dataset.morse04Edge!==edge)return;
    node.classList.add('morse04-auto-host');
    node.dataset.morse04Edge=edge;
    node.dataset.morsePlacement=edge;
    node.dataset.morseAutoMessage=message;
    originalBoundary(node,edge);
    const svg=stroke(node,'morse04-seam');
    svg.classList.add('morse04-seam');
    track(node);
    state.auto.add(node);
    inkLine(node,':scope > svg.morse04-seam',message);
  }
  function forms(){
    for(const [selector,key,fallback]of [
      ['footer.footer-04 .f4-form','morseNewsletter','STAY CLOSE'],
      ['footer.footer-04 .f4-localization-form','morseLocale','04 TRI']
    ])document.querySelectorAll(selector).forEach(node=>makeSeam(node,'bottom',value(key,fallback)));
  }
  function isEligible(el){
    if(!el||el.nodeType!==1)return false;
    if(el.closest('.product-card, .patch04, .product-media-container, .product-details .accordion, header, nav, aside[role], dialog, button, label, [role="button"], #cart-drawer'))return false;
    if(el.matches('input, select, a, button, img, video, canvas, svg, hr'))return false;
    if(el.closest('footer.footer-04') && !el.matches('.f4-news, .f4-base,.f4-pay-row,.f4-form,.f4-localization-form'))return false;
    const rect=el.getBoundingClientRect();
    if(rect.width<Math.max(140,window.innerWidth*.25)||rect.height<6)return false;
    return true;
  }
  function wideDividers(){
    const groups=[
      ['main#MainContent .production-progress-04-block .pp04-card','bottom','morseProgress','04 STUDIOS'],
      ['main#MainContent .pp04__journey','bottom','morseGlobal','04 STUDIOS'],
      ['main#MainContent .pp04__updates','bottom','morseGlobal','04 STUDIOS'],
      ['footer.footer-04 .f4-news','top','morseNewsletter','STAY CLOSE'],
      ['footer.footer-04 .f4-base','top','morseBottom','04 STUDIOS'],
      ['footer.footer-04 .f4-pay-row','top','morseBottom','04 STUDIOS'],
      ['[data-morse04-divider]','bottom','morseGlobal','04 STUDIOS']
    ];
    groups.forEach(([sel,edge,key,fallback])=>{
      document.querySelectorAll(sel).forEach(el=>makeSeam(el,edge,value(key,fallback)));
    });
    // Theme-level discovery for DECORATIVE structural dividers only.
    // It intentionally excludes every control, progress meter, product card and image frame.
    const candidates=document.querySelectorAll(
      'main#MainContent :is(.shopify-section, section, article, .group-block, '+
      '[class*="__divider"],[class*="__separator"],[class*="__rule"],'+
      '[class*="-divider"],[class*="-separator"],.section-border,.section-rule)');
    let count=0;
    candidates.forEach(el=>{
      if(count>=70||!isEligible(el)||state.auto.has(el))return;
      const st=getComputedStyle(el);
      if(st.display==='none'||st.visibility==='hidden'||st.position==='sticky'||st.position==='fixed'||st.position==='absolute')return;
      let edge='';
      if(parseFloat(st.borderBottomWidth)>0 && ['solid','dashed'].includes(st.borderBottomStyle))edge='bottom';
      else if(parseFloat(st.borderTopWidth)>0 && ['solid','dashed'].includes(st.borderTopStyle))edge='top';
      if(!edge)return;
      // Existing dividers and content zones should not produce duplicate Morse.
      if(el.closest('.accordion,.pp04-card,.pp04__journey,.pp04__updates,.production-progress-04-block'))return;
      if(el.querySelector(':scope > .morse04-seam'))return;
      makeSeam(el,edge,value('morseGlobal','04 STUDIOS'));count++;
    });
  }
  function restoreAuto(){
    for(const el of [...state.auto]){
      if(!el.isConnected){state.auto.delete(el);continue;}
      const saved=state.original.get(el);
      for(const edge of ['top','bottom']){
        if(saved?.[edge]){
          const {name,value,priority}=saved[edge];
          if(value)el.style.setProperty(name,value,priority);
          else el.style.removeProperty(name);
        }
      }
      el.querySelector(':scope > svg.morse04-seam')?.remove();
      el.classList.remove('morse04-auto-host');
      delete el.dataset.morse04Edge;
      delete el.dataset.morsePlacement;
      delete el.dataset.morseAutoMessage;
      state.auto.delete(el);
      state.original.delete(el);
    }
  }
  function redraw(node){
    if(node.classList.contains('morse04-auto-host')){
      inkLine(node,':scope > svg.morse04-seam',node.dataset.morseAutoMessage);
    } else if(node.matches('summary.details__header')){
      inkLine(node,':scope > svg.morse04-divider',node.dataset.morseMessage);
    } else if(node.matches('summary.f4-acc__summary')){
      inkLine(node,':scope > svg.morse04-footer--heading',
        node.querySelector('svg.morse04-footer--heading')?.dataset.morseMessage);
    } else if(node.matches('.patch04,.pdp04-moss-patch .add-to-cart-button,.pc04-controls button,.pdpref04-dock__buy')){
      edge(node,node.matches('.patch04') ? node.dataset.morseMessage||value('morsePatchMessage','04 STUDIOS')
        :value('morseButtonMessage','BUY EARLY'));
    }
  }
  function sync(){
    const overall=reflect();
    if(!overall){restoreAuto();return;}
    if(on('morseProduct'))product();
    if(on('morseFooter'))footerHeadings();
    if(on('morsePatch')||on('morseButtons'))edges();
    if(on('morseAuto'))wideDividers();
    if(on('morseForms'))forms();
    if(!on('morseAuto')&&!on('morseForms'))restoreAuto();
    else for(const el of [...state.auto]){
      if(!el.isConnected){state.auto.delete(el);continue;}
      const isForm=el.matches('footer.footer-04 .f4-form,footer.footer-04 .f4-localization-form');
      if((isForm&&!on('morseForms'))||(!isForm&&!on('morseAuto'))){
        const saved=state.original.get(el), edge=el.dataset.morse04Edge;
        if(saved?.[edge]){
          const {name,value,priority}=saved[edge];
          if(value)el.style.setProperty(name,value,priority);else el.style.removeProperty(name);
        }
        el.querySelector(':scope > svg.morse04-seam')?.remove();
        el.classList.remove('morse04-auto-host');
        delete el.dataset.morse04Edge;
        state.auto.delete(el);state.original.delete(el);
      }
    }
  }
  function queue(){
    if(state.queued)return;
    state.queued=true;
    requestAnimationFrame(()=>{state.queued=false;sync();});
  }
  function boot(){
    sync();
    const mo=new MutationObserver(records=>{
      const should=records.some(record=>{
        if(record.type==='attributes')return record.target.id==='MorseSystem04';
        return [...record.addedNodes].some(n=>n.nodeType===1 &&
          !n.matches?.('.morse04-seam,.morse04-edge,.morse04-divider,.morse04-footer,.morse04-rest')&&
          (n.matches?.('section,article,footer,variant-picker,accordion-custom,.shopify-section,.patch04,.f4-form,.f4-localization-form,[data-morse04-divider]') ||
           n.querySelector?.('footer,variant-picker,accordion-custom,.patch04,.f4-form,.f4-localization-form,[data-morse04-divider]')));
      });
      if(should)queue();
    });
    mo.observe(document.body,{subtree:true,childList:true,attributes:true,attributeFilter:['data-morse-enabled','data-morse-auto','data-morse-global','data-morse-product','data-morse-footer','data-morse-forms']});
    window.addEventListener('resize',queue,{passive:true});
    document.addEventListener('shopify:section:load',queue);
    document.addEventListener('shopify:section:unload',queue);
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});
  else boot();
})();
